<template>
  <div>
    <!-- Header -->
    <div class="d-flex justify-content ml-2 align-items-center my-2">
      <button class="btn btn-sm rounded-circle p-25 cursor-pointer" :class="isDark ? 'btn-danger' : 'btn-primary'"
        @click="goBack()">
        <i class="feather icon-arrow-left arrow-icon"></i>
      </button>
      <div class="ml-1 text-capitalize text-dark cursor-pointer goback-text" @click="goBack">
        {{ $t("actions.go_back") }}
      </div>
    </div>

    <b-card class="m-2" no-body>
      <b-card-body>
        <div class="m-2">
          <h1 class="mb-2">{{ $t("match_rules.general") }}</h1>
          <validation-observer ref="refFormObserver">
            <b-row>
              <b-col cols="12" md="3">
                <validation-provider #default="validationContext" :name="$t('match_rules.name')" rules="required">
                  <b-form-group :label="$t('match_rules.name_label')">
                    <b-form-input v-model="formData.name" :placeholder="$t('match_rules.name')" />
                    <b-form-invalid-feedback :state="getValidationState(validationContext)">{{
                      validationContext.errors[0]
                      }}</b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12" md="3">
                <validation-provider #default="validationContext" :name="$t('match_rules.description')"
                  rules="required">
                  <b-form-group :label="$t('match_rules.description_label')">
                    <b-form-input v-model="formData.description" :placeholder="$t('match_rules.description')" />
                    <b-form-invalid-feedback :state="getValidationState(validationContext)">{{
                      validationContext.errors[0]
                      }}</b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12" md="3">
                <validation-provider #default="validationContext" :name="$t('match_rules.tenant')" rules="required">
                  <b-form-group :label="$t('match_rules.tenant_label')">
                    <v-select multiple :options="tenantOptions" v-model="formData.tenantID" class="w-100"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :reduce="val => val.value"
                      :placeholder="$t('match_rules.tenant')" append-to-body />
                    <b-form-invalid-feedback :state="getValidationState(validationContext)">{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12" md="3">
                <validation-provider #default="validationContext" :name="$t('match_rules.alarm_type')" rules="required">
                  <b-form-group :label="$t('match_rules.alarm_type_label')">
                    <v-select v-model="alarmTypeForm.value" :options="AlarmTypeOptions" class="w-100"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'" :reduce="val => val.value"
                      :placeholder="$t('match_rules.alarm_type')" />
                    <b-form-invalid-feedback :state="getValidationState(validationContext)">{{
                      validationContext.errors[0]
                    }}</b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
            </b-row>
          </validation-observer>

          <b-row>
            <div class="d-flex align-items-start">
              <div class="ml-1">
                <h1 class="mt-3 mb-2">{{ $t("match_rules.providers") }}</h1>
                <ProviderButton @save="addOrUpdateRule" :editItem="editingItem" :providerItem="providerRule" />
                <div v-if="providerRule">
                  <p style="font-size: larger;">
                    {{ $t("match_rules_card.provider_applied") }}
                  </p>
                  <div class="d-flex align-items-start">
                    <div>
                      <div class="d-flex align-items-start">
                        <div class="rounded mr-2 p-1 px-3"
                          style="background-color: #E8E8E8; font-weight: bolder; white-space: nowrap; display: inline-block;">
                          {{ $t(`match_rules_action.${providerRule.action}`) }}
                        </div>
                        <!-- Providers list in line -->
                        <div
                          class="rounded p-1 px-3"
                          style="background-color: #E8E8E8; font-weight: bolder; white-space: nowrap; overflow-x: auto;"
                        >
                          {{ Array.isArray(providerRule.value) ? providerRule.value.join(", ") : providerRule.value }}
                        </div>
                      </div>
                    </div>

                    <!-- Actions dropdown -->
                    <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
                      <template #button-content>
                        <feather-icon icon="MoreVerticalIcon" size="24" class="text-dark" data-cy="actions" />
                      </template>

                      <b-dropdown-item @click="editProvider()">
                        <feather-icon icon="EditIcon" />
                        <span class="align-middle ml-50 text-capitalize">{{ $t("actions.edit") }}</span>
                      </b-dropdown-item>

                      <b-dropdown-item @click="deleteProvider()">
                        <feather-icon icon="TrashIcon" />
                        <span class="align-middle ml-50 text-capitalize">{{ $t("actions.delete") }}</span>
                      </b-dropdown-item>
                    </b-dropdown>

                  </div>
                </div>
              </div>
              <div class="ml-3">
                <h1 class="mt-3 mb-2">{{ $t("match_rules.camera") }}</h1>
                <CameraButton @save="addOrUpdateRule" :editItem="editingItem" :providerItem="cameraRule"
                  :tenantIds="formData.tenantID" />
                <div v-if="cameraRule">
                  <p style="font-size: larger;">
                    {{ $t("match_rules_card.camera_applied") }}
                  </p>
                  <div class="d-flex align-items-start">
                    <div>
                      <div class="d-flex align-items-start">
                        <div class="rounded mr-2 p-1 px-3"
                          style="background-color: #E8E8E8; font-weight: bolder; white-space: nowrap; display: inline-block;">
                          {{ $t(`match_rules_action.${cameraRule.action}`) }}
                        </div>

                        <!-- Cámara list con estilo tipo tabla -->
                        <div class="rounded p-2 w-100 d-flex flex-wrap"
                          style="background-color: #E8E8E8; font-weight: bolder; max-height: 150px; overflow-y: auto;">
                          <template v-if="Array.isArray(cameraRule.value)">
                            <div v-for="(id, i) in cameraRule.value" :key="i" class="mr-3 mb-1"
                              style="min-width: 150px;">
                              {{ $store.getters["alarm_cameras/getSelectedItems"].get(parseInt(id, 10)) }}
                            </div>
                          </template>
                          <template v-else>
                            <div style="min-width: 150px;">
                              {{ $store.getters["alarm_cameras/getSelectedItems"].get(parseInt(cameraRule.value, 10)) }}
                            </div>
                          </template>
                        </div>

                      </div>
                    </div>

                    <!-- Actions dropdown -->
                    <b-dropdown variant="link" no-caret :right="$store.state.appConfig.isRTL">
                      <template #button-content>
                        <feather-icon icon="MoreVerticalIcon" size="24" class="text-dark" data-cy="actions" />
                      </template>

                      <b-dropdown-item @click="editCamera()">
                        <feather-icon icon="EditIcon" />
                        <span class="align-middle ml-50 text-capitalize">{{ $t("actions.edit") }}</span>
                      </b-dropdown-item>

                      <b-dropdown-item @click="deleteCamera()">
                        <feather-icon icon="TrashIcon" />
                        <span class="align-middle ml-50 text-capitalize">{{ $t("actions.delete") }}</span>
                      </b-dropdown-item>
                    </b-dropdown>

                  </div>
                </div>
              </div>
            </div>
          </b-row>

          <b-row class="justify-content-center justify-content-md-end mt-3">
            <b-col cols="12" md="auto">
              <b-button class="w-100 mt-1" variant="primary" @click="goBack">
                {{ $t("actions.cancel") }}
              </b-button>
            </b-col>
            <b-col cols="12" md="auto">
              <b-button @click="saveRule" class="w-100 mt-1" variant="success">
                {{ $t("actions.save") }}
              </b-button>
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </b-card>


    <b-card class="m-2" no-body>
      <b-card-body>
        <div class="m-2">
          <b-row class="justify-content-center justify-content-md-end">
            <b-col>
              <h1>{{ $t("match_rules.table") }}</h1>
              <p style="font-size: larger;">{{ $t("match_rules_card.match_rule_table") }}</p>
            </b-col>
            <b-col cols="12" md="auto">
              <MatchRuleButton @save="addOrUpdateRule" :editItem="editingItem" :providerItem="providerRule" />
            </b-col>
          </b-row>
        </div>
        <!-- Stack de elementos -->
        <div class="mx-2 mb-2">
          <b-row>
            <b-col cols="12">
              <AlarmRuleStack :items="formData.matchRules" @delete="deleteRule" @edit="editRule" />
            </b-col>
          </b-row>
        </div>
      </b-card-body>
    </b-card>


  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BFormInvalidFeedback,
} from "bootstrap-vue";

import AlarmRuleStack from "@/views/alarm/rules/Stack.vue";
import MatchRuleButton from "@/views/alarm/rules/MatchRuleButton.vue";
import CameraButton from "@/views/alarm/rules/CameraButton.vue";
import ProviderButton from "@/views/alarm/rules/ProviderButton.vue";

import vSelect from "vue-select";
import store from "@/store";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { computed, reactive, ref, onMounted, watch } from "@vue/composition-api";
import router from "@/router";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import formValidation from "@core/comp-functions/forms/form-validation";


export default {
  name: "RuleGrid",
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BFormInvalidFeedback,
    vSelect,
    AlarmRuleStack,
    MatchRuleButton,
    ValidationProvider,
    ValidationObserver,
    ProviderButton,
    CameraButton
  },
  setup(_, context) {

    // form validation component
    const { refFormObserver, getValidationState } = formValidation();

    const isDark = computed(() => store.state.appConfig.layout.skin === "dark");
    const toast = useToast();


    // form for alarm type opts
    const alarmTypeForm = reactive({
      action: 'equal_to',
      field: 'alarmType',
      valueType: 'int',
      value: [],
    });

    // camera filter
    const filter = {
      "page[n]": 1,
      max: 1000,
      q: "",
      sortBy: "id",
      sortDesc: true,
      "with[]": "units",
      "tenants[]": ""
    };

    // Stores the item being edited
    const editingItem = ref(null);
    // Stores the index of the item being edited in the rules array
    const editingIndex = ref(null);

    // Navigates back to the previous view
    const goBack = () => context.root.$router.push({ name: "alarm-rules" });

    // Retrieves the list of tenants from Vuex
    const tenantOptions = computed(() => {
      return store.getters["tenant/getSelectedItems"].map(option => ({
        label: option.label,
        value: option.value,
      }));
    });

    // Retrieves the list of alarm types from Vuex
    const AlarmTypeOptions = computed(() => {
      return store.getters["alarm_types/getSelectedItems"].map(option => ({
        label: option.label,
        value: option.value,
      }));
    })

    // Retrieves the provider rule from the matchRules array to handle conditional rendering and editing logic.
    const providerRule = computed(() => {
      return formData.matchRules.find(rule => rule.field === "provider");
    });

    // Retrieves the camera rule from the matchRules array to handle conditional rendering and editing logic.
    const cameraRule = computed(() => {
      return formData.matchRules.find(rule => rule.field === "camera");
    });

    // Retrieves the user information from Vuex
    const userData = computed(() => store.getters["user/getUser"]);

    // Main form data
    const formData = reactive({
      name: "",
      description: "",
      userID: userData.value.id,
      tenantID: [],
      matchRules: [],
    });

    // Adds or updates a rule in the matchRules array
    const addOrUpdateRule = (rule) => {
      const index = formData.matchRules.findIndex(r => r.field === rule.field);

      if (index !== -1) {
        formData.matchRules.splice(index, 1, rule);
      } else {
        formData.matchRules.push(rule);
      }

      editingItem.value = null;
    };

    // Removes a rule from the matchRules array
    const deleteRule = (index) => {
      formData.matchRules.splice(index, 1);
    };

    // Prepares a rule item for editing by setting its data and index
    const editRule = (index) => {
      editingItem.value = { ...formData.matchRules[index] };
      editingIndex.value = index;
    };

    // Prepares a provider item for editing or saving
    const editProvider = () => {
      const index = formData.matchRules.findIndex(r => r.field === 'provider');
      if (index !== -1) {
        editingItem.value = { ...formData.matchRules[index] };
        editingIndex.value = index;
      }
    };

    // Removes a provider from the matchRules array
    const deleteProvider = () => {
      const index = formData.matchRules.findIndex(r => r.field === 'provider');
      if (index !== -1) {
        formData.matchRules.splice(index, 1);
        if (editingIndex.value === index) {
          editingItem.value = null;
          editingIndex.value = null;
        }
      }
    };

    // Prepares a camera item for editing or saving
    const editCamera = () => {
      const index = formData.matchRules.findIndex(r => r.field === 'camera');
      if (index !== -1) {
        editingItem.value = { ...formData.matchRules[index] };
        editingIndex.value = index;
      }
    };

    // Removes a camera from the matchRules array
    const deleteCamera = () => {
      const index = formData.matchRules.findIndex(r => r.field === 'camera');
      if (index !== -1) {
        formData.matchRules.splice(index, 1);
        if (editingIndex.value === index) {
          editingItem.value = null;
          editingIndex.value = null;
        }
      }
    };

    // Prepares a complete rule for saving
    const saveRule = async () => {
      const validation = await refFormObserver.value.validate();
      if (!validation) return;

      // cast camera proxy id to string
      formData.matchRules = formData.matchRules.map(rule => {
        if (["camera", "alarmType"].includes(rule.field)) {
          const valueArray = Array.isArray(rule.value) ? rule.value : [rule.value];
          return {
            ...rule,
            value: valueArray.map(id => String(id)),
          };
        }
        return rule;
      });
      // req represents request body
      const req = {
        name: formData.name,
        description: formData.description,
        userID: String(formData.userID),
        tenantID: formData.tenantID,
        matchRules: formData.matchRules,
      };

      console.log(req)

      let dispatch;

      // match rule validation
      if (formData.matchRules.length == 0) {
        toast({
          component: ToastificationContent,
          props: {
            title: context.parent.$i18n.t("match_rules.min_match_rule"),
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
      } else {
        if (!router.currentRoute.params.id_rule) {
          dispatch = store.dispatch("alarm_rules/add", req);
        } else {
          const data = {
            id: router.currentRoute.params.id_rule,
            params: req,
          };
          dispatch = store.dispatch("alarm_rules/edit", data);
        }
        dispatch
          .then((response) => {
            if (!router.currentRoute.params.id_rule) {
              toast({
                component: ToastificationContent,
                props: {
                  title: context.parent.$i18n.t("messages.edit_save.successfully_created"),
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
            } else {
              toast({
                component: ToastificationContent,
                props: {
                  title: context.parent.$i18n.t("messages.edit_save.updated_successfully"),
                  icon: "CheckIcon",
                  variant: "success",
                },
              });
            }
          })
          .catch((error) => {
            toast({
              component: ToastificationContent,
              props: {
                title: context.parent.$i18n.t("messages.error.server_error"),
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          });
      }
    };

    // Fetches tenants when the component is mounted
    onMounted(() => {

      store.dispatch("tenant/getAll");
      store.dispatch("alarm_types/getAll")
      store.dispatch("alarm_cameras/getAll", filter)
    });

    // Assigns user ID from Vuex when the component is mounted
    onMounted(() => {
      const user = userData.value;
      if (user && user.id) {
        formData.userID = user.id;
      }
    });

    // Fetches rule details when the component is mounted
    onMounted(() => {
      const ruleId = router.currentRoute.params.id_rule
      if (ruleId) {
        store.dispatch('alarm_rules/getRule', ruleId)
          .then(response => {

            // cast int values to string
            const normalizedRules = response.matchRules.map(rule => {
              if (["camera", "alarmType"].includes(rule.field)) {
                const valueArray = Array.isArray(rule.value) ? rule.value : [rule.value];
                return {
                  ...rule,
                  value: valueArray.map(id => parseInt(id, 10)),
                };
              }
              return rule;
            });

            //set rule data into main view
            formData.name = response.name;
            formData.description = response.description;
            formData.userID = response.userID;
            formData.tenantID = response.tenantID;
            formData.matchRules.splice(0, formData.matchRules.length, ...normalizedRules);

            // set alarm type into main view
            const alarmTypeRule = normalizedRules.find(rule => rule.field === "alarmType");
            if (alarmTypeRule) {
              alarmTypeForm.action = alarmTypeRule.action;
              alarmTypeForm.field = alarmTypeRule.field;
              alarmTypeForm.valueType = alarmTypeRule.valueType;
              alarmTypeForm.value = Array.isArray(alarmTypeRule.value)
                ? alarmTypeRule.value[0]
                : alarmTypeRule.value;
            }
          })
          .catch(error => {
            console.error(error)
            toast({
              component: ToastificationContent,
              props: {
                title: context.parent.$i18n.t("messages.error.server_error"),
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            });
          });
      }
    });


    watch(() => alarmTypeForm.value, (newValue) => {
      if (!newValue) return;

      const index = formData.matchRules.findIndex(rule => rule.field === "alarmType");
      const newRule = {
        ...alarmTypeForm,
        value: newValue,
      };

      if (index !== -1) {
        formData.matchRules.splice(index, 1, newRule);
      } else {
        formData.matchRules.push(newRule);
      }
    });

    return {
      isDark,
      formData,
      goBack,
      tenantOptions,
      editingItem,
      addOrUpdateRule,
      deleteRule,
      editRule,
      saveRule,
      refFormObserver,
      getValidationState,
      providerRule,
      editProvider,
      deleteProvider,
      cameraRule,
      editCamera,
      deleteCamera,
      AlarmTypeOptions,
      alarmTypeForm,
    };
  },
};

</script>

<style lang="scss" scoped>
.arrow-icon {
  font-size: 12px;
}

.goback-text {
  position: relative;
  top: 0.1rem;
  margin-left: 0.5rem !important;
}
</style>